/**
 * TEMPLATE ISSUES
 * Single list of the problems found in a questionnaire, shared by the editor (notifications, counters, banner)
 * and the project checks. Each rule is a pure function (see utils) wrapped here with its texts.
 */

import { createContext } from "react"
import { getTopicPositions } from "@/utils/topic-position.utils"
import { snakeCase } from "lodash"
import { t } from "@/translate/t"
import { Topic } from "./topic.types"
import { getChoicePropositionIssues, getChoiceTooFewPropositions } from "@/utils/choice-propositions.utils"
import { getScaleLabelDuplicates, getScaleLabelMissing } from "@/utils/scale-labels.utils"
import { getDuplicatePrimaryQuestions } from "@/utils/duplicate-questions.utils"
import { getAxisNameIssues } from "@/utils/axis-names.utils"
import { getRpsTagShortages } from "@/utils/rps-tags.utils"
import { getReverseSuspect, getValidMemory } from "@/utils/reverse-suspect.utils"

export type TemplateIssueKind =
  | "secondary-trigger"
  | "unnamed-topic"
  | "scale-labels"
  | "scale-labels-missing"
  | "choice-empty"
  | "choice-duplicate"
  | "choice-too-few"
  | "draft-question"
  | "untranslated"
  | "rps-tags"
  | "axis-unnamed"
  | "axis-empty"
  | "duplicate-question"
  | "question-empty"
  | "reverse-suspect"

export interface TemplateIssue {
  id: string //Unique, starts with the topic id (or "template:" if the issue is not related to a topic)
  kind: TemplateIssueKind
  severity: "error" | "warning" //Error blocks the launch of a project and cannot be ignored
  topicId: string | null
  language?: string //Language to open in the editor (missing translations)
  label: string //Short title (name of the question)
  group?: string //"Topic · question" : the issues of the same question are displayed together
  description: string //What is wrong
  intro?: string //Start of the sentence when the detail is displayed as a list
  details?: string[] //One line each (third level of the list)
  message: string //Same in one sentence (project checks)
  questionId?: string //Question concerned (suggestion of the AI : the editor can apply it)
  suggestedReverse?: boolean //Value of the option "reverse" suggested by the AI
  needsConfirmation?: boolean //The editor asks a confirmation before editing this question (question of IDTree, locked template)
}

//Problems to display in the editor (see utils/issues-baseline.utils.ts), provided by the route of the editor
export const TemplateIssuesContext = createContext<TemplateIssue[]>([])

//Bold part of a message : **text** (rendered by the project step errors widget)
function bold(text: string): string {
  return "**" + text + "**"
}

//List every issue of a questionnaire. Languages : languages of the template (or of the project)
export function getTemplateIssues(
  topics: Topic[],
  options: { [key: string]: any },
  languages: string[],
  settings: {
    rps?: boolean
    axes?: { id: string; name?: { [language: string]: string } }[]
    ai?: boolean //The suggestions of the AI are shown (module of the account, not turned off by the person)
    aiMemories?: { [questionId: string]: any } //Answers just received from the server, not yet in the questions (they win)
    isSupervisor?: boolean
    isLocked?: boolean
  } = {} //rps : the account can use the RPS indicators, axes : to check their names
): TemplateIssue[] {
  const issues: TemplateIssue[] = []
  const language = languages[0]

  //Axes without name (every language, empty or not) : red, named in some languages only : part of the missing translations
  const axes = settings.axes || []
  const axisIssues = getAxisNameIssues(
    axes,
    languages,
    topics.map((x) => x.AxisId).filter((x): x is string => !!x)
  )
  const topicNames: { [id: string]: string } = {}
  topics.forEach((x) => {
    topicNames[x.id] = x.name?.[language] || t("topic_undefined")
  })
  //An unnamed topic is told apart by its position in the editor ("#x.2") : two of them would otherwise look the same
  const positions = getTopicPositions(
    topics,
    axes.map((x) => x.id)
  )
  const unnamedTopicIds = new Set(topics.filter((x) => !x.name?.[language]).map((x) => x.id))
  const groupOf = (topicId: string, label: string) =>
    (unnamedTopicIds.has(topicId) ? t("topic_undefined") + " " + positions[topicId] : topicNames[topicId]) +
    " · " +
    label
  const firstTopicOfAxis = (axisId: string) => topics.find((x) => x.AxisId === axisId)

  topics
    .map((x) => new Topic(x))
    .forEach((topic) => {
      const topicName = topic.name[language] || topic.label || t("topic_undefined") + " " + positions[topic.id] //An unnamed topic is told by its position (an empty bold would show its stars)

      //A topic without any name : red flag
      if (topic.isUnnamedCritical(languages)) {
        const description = t("template_issue_topic_unnamed")
        issues.push({
          id: topic.id + ":name",
          kind: "unnamed-topic",
          severity: "error",
          topicId: topic.id,
          group: groupOf(topic.id, topic.questionPrimary?.name[language] || t("question_new")),
          label: topic.questionPrimary?.name[language] || t("question_new"),
          description,
          message: description
        })
      }

      //A secondary question that would never be triggered
      topic.questionsSecondaryNeverTriggered.forEach((question) => {
        issues.push({
          id: topic.id + ":" + question.id,
          kind: "secondary-trigger",
          severity: "error",
          topicId: topic.id,
          group: groupOf(topic.id, question.name[language] || t("question_new")),
          label: question.name[language] || t("question_new"),
          description: t("template_issue_secondary_trigger", { topic: topicName }),
          message: t("project_error_template_secondary_trigger_choice", {
            topic: topicName,
            question: question.name[language] || t("question_new")
          })
        })
      })

      //One language : a question without text is an error (several languages : it is a missing translation, below). Drafts are ignored
      if (languages.length === 1 && !topic.getIsEmpty(language)) {
        topic.Questions.filter((question) => !question.draft && question.isEmpty(language)).forEach((question) => {
          const description = t("template_issue_question_empty")
          issues.push({
            id: topic.id + ":" + question.id + ":text",
            kind: "question-empty",
            severity: "error",
            topicId: topic.id,
            group: groupOf(topic.id, t("question_new")),
            label: t("question_new"),
            description,
            message: bold(topicName) + " : " + description
          })
        })
      }

      //Answers of a QCM : empty or identical
      topic.Questions.forEach((question) => {
        const questionLabel = question.name[language] || t("question_new")
        const choice = getChoicePropositionIssues(question, languages)

        if (choice.emptyCount > 0) {
          const description = t("template_issue_choice_empty" + (choice.emptyCount > 1 ? "_multiple" : ""), {
            count: choice.emptyCount
          })
          issues.push({
            id: topic.id + ":" + question.id + ":empty",
            kind: "choice-empty",
            severity: "error",
            topicId: topic.id,
            group: groupOf(topic.id, questionLabel),
            label: questionLabel,
            description,
            message: bold(topicName) + " · " + bold(questionLabel) + " : " + description
          })
        }

        const tooFew = getChoiceTooFewPropositions(question)
        if (tooFew) {
          const description = t(
            tooFew.limit ? "template_issue_choice_too_few_limit" : "template_issue_choice_too_few",
            {
              count: tooFew.count,
              limit: tooFew.limit
            }
          )
          issues.push({
            id: topic.id + ":" + question.id + ":too-few",
            kind: "choice-too-few",
            severity: "error",
            topicId: topic.id,
            group: groupOf(topic.id, questionLabel),
            label: questionLabel,
            description,
            message: bold(topicName) + " · " + bold(questionLabel) + " : " + description
          })
        }

        choice.duplicates.forEach((duplicate) => {
          const description = t("template_issue_choice_duplicate", {
            language: duplicate.language.toUpperCase(),
            label: duplicate.label
          })
          issues.push({
            id: topic.id + ":" + question.id + ":duplicate-" + duplicate.language,
            kind: "choice-duplicate",
            severity: "error",
            topicId: topic.id,
            group: groupOf(topic.id, questionLabel),
            label: questionLabel,
            description,
            message: bold(topicName) + " · " + bold(questionLabel) + " : " + description
          })
        })
      })
    })

  axisIssues.unnamed.forEach((axisId) => {
    const description = t("template_issue_axis_unnamed")
    const label = t("axis") + " " + (axes.findIndex((x) => x.id === axisId) + 1)
    issues.push({
      id: "template:axis-" + axisId,
      kind: "axis-unnamed",
      severity: "error",
      topicId: firstTopicOfAxis(axisId)?.id ?? null,
      group: label,
      label,
      description,
      message: bold(label) + " : " + description
    })
  })

  //Axes without any question (named or not) : error, an axis has to contain questions
  const usedAxes = topics.map((x) => x.AxisId)
  axes
    .filter((axis) => !usedAxes.includes(axis.id))
    .forEach((axis) => {
      const index = axes.findIndex((x) => x.id === axis.id) + 1
      const label = (axis.name?.[language] || "").trim() || t("axis") + " " + index
      const description = t("template_issue_axis_empty")
      issues.push({
        id: "template:axis-empty-" + axis.id,
        kind: "axis-empty",
        severity: "error",
        topicId: null,
        group: label,
        label,
        description,
        message: bold(label) + " : " + description
      })
    })

  //Questions still in draft (the draft can no longer be set, old ones are ignored by the projects) : one warning
  const draftTopics = topics.map((x) => new Topic(x)).filter((topic) => topic.countDraft > 0)
  if (draftTopics.length > 0) {
    const count = draftTopics.reduce((sum, topic) => sum + topic.countDraft, 0)
    const title = t("template_issue_draft_title")
    const description = t("template_issue_draft_question" + (count > 1 ? "_multiple" : ""), { count })
    issues.push({
      id: "template:drafts",
      kind: "draft-question",
      severity: "warning",
      topicId: draftTopics[0].id,
      label: title,
      description,
      message: bold(title) + " : " + description
    })
  }

  //Missing translations : one single issue for every language (a question or a topic without text blocks the launch).
  //The id is stable so that the issue stays while the count goes down as translations are done.
  //Draft questions are ignored.
  if (languages.length > 1) {
    const topicsList = topics.map((x) => new Topic(x))
    let total = 0
    let first: { topic: Topic; language: string } | null = null
    const parts: string[] = []

    languages.forEach((lg) => {
      const questions = topicsList
        .map((topic) => ({ topic, count: topic.getEmptyQuestions(lg).filter((q) => !q.draft).length }))
        .filter((x) => x.count > 0 && !x.topic.getIsEmpty(lg))
      const unnamed = topicsList.filter(
        (topic) => topic.getIsEmpty(lg) && !topic.isUnnamedCritical(languages) && topic.Questions.some((q) => !q.draft)
      )
      const questionsCount = questions.reduce((sum, x) => sum + x.count, 0)
      const axesMissing = axisIssues.missing.find((x) => x.language === lg)?.ids || []
      if (questionsCount === 0 && unnamed.length === 0 && axesMissing.length === 0) return

      total += questionsCount + unnamed.length + axesMissing.length
      const details: string[] = []
      if (questionsCount > 0) {
        details.push(
          t("template_issue_untranslated_questions" + (questionsCount > 1 ? "_multiple" : ""), {
            count: questionsCount
          })
        )
      }
      if (unnamed.length > 0) {
        details.push(
          t("template_issue_untranslated_topics" + (unnamed.length > 1 ? "_multiple" : ""), { count: unnamed.length })
        )
      }
      if (axesMissing.length > 0) {
        details.push(
          t("template_issue_untranslated_axes" + (axesMissing.length > 1 ? "_multiple" : ""), {
            count: axesMissing.length
          })
        )
      }
      parts.push(t("template_issue_untranslated_lang", { country: lg.toUpperCase(), details: details.join(", ") }))

      if (!first) {
        const topic =
          questions.length > 0
            ? questions[0].topic
            : unnamed.length > 0
              ? unnamed[0]
              : topicsList.find((x) => x.AxisId === axesMissing[0]) || topicsList[0]
        first = { topic, language: lg }
      }
    })

    if (total > 0 && first) {
      const found = first as { topic: Topic; language: string }
      const title = t("template_issue_untranslated_title", { count: total })
      issues.push({
        id: "template:untranslated",
        kind: "untranslated",
        severity: "error",
        topicId: found.topic.id,
        language: found.language,
        label: title,
        description: parts.join(" · "),
        details: parts,
        message: bold(title) + " : " + parts.join(" · ")
      })
    }
  }

  //Two cards asking the same primary question (warning : can be ignored)
  getDuplicatePrimaryQuestions(
    topics.map((x) => new Topic(x)),
    languages
  ).forEach((duplicate) => {
    const description = t("template_issue_duplicate_question", {
      language: duplicate.language.toUpperCase(),
      count: duplicate.topicIds.length
    })
    issues.push({
      id: duplicate.topicIds[0] + ":duplicate-question-" + duplicate.language,
      kind: "duplicate-question",
      severity: "warning",
      topicId: duplicate.topicIds[0],
      group: groupOf(duplicate.topicIds[0], duplicate.label),
      language: duplicate.language,
      label: duplicate.label,
      description,
      message: bold(duplicate.label) + " : " + description
    })
  })

  //Two identical labels in an answer scale (edited in the parameters of the template)
  getScaleLabelDuplicates(options, languages).forEach((duplicate) => {
    const description = t("template_issue_scale_labels", {
      count: duplicate.count,
      language: duplicate.language.toUpperCase(),
      label: duplicate.label
    })
    issues.push({
      id: "template:scale-" + duplicate.count + "-" + duplicate.language,
      kind: "scale-labels",
      severity: "error",
      topicId: null,
      label: t("template_issue_scale_labels_title"),
      description,
      message: bold(t("template_issue_scale_labels_title")) + " : " + description
    })
  })

  //Custom labels of an answer scale not translated in every language (warning : the default text is displayed)
  getScaleLabelMissing(options, languages).forEach((missing) => {
    const description = t("template_issue_scale_labels_missing", {
      count: missing.count,
      language: missing.language.toUpperCase(),
      missing: missing.missing
    })
    issues.push({
      id: "template:scale-missing-" + missing.count + "-" + missing.language,
      kind: "scale-labels-missing",
      severity: "warning",
      topicId: null,
      label: t("template_issue_scale_labels_title"),
      description,
      message: bold(t("template_issue_scale_labels_title")) + " : " + description
    })
  })

  //Not enough tagged questions to get an RPS result (warning, only for accounts with the RPS module)
  if (settings.rps) {
    const questions = topics.flatMap((topic) => new Topic(topic).Questions)
    const shortages = getRpsTagShortages(questions)
    if (shortages.length > 0) {
      const details = shortages.map(
        (shortage) => bold(t("rps_" + snakeCase(shortage.tag))) + " " + shortage.count + "/" + shortage.min
      )
      const description = t("template_issue_rps_tags", { dimensions: details.join(" · ") })
      issues.push({
        id: "template:rps",
        kind: "rps-tags",
        severity: "warning",
        topicId: null,
        label: t("template_issue_rps_title"),
        description,
        intro: t("template_issue_rps_intro"),
        details,
        message: bold(t("template_issue_rps_title")) + " : " + description
      })
    }
  }

  //Questions probably set the wrong way round, according to the AI of the project assistant (warning, never blocking) : what the
  //server remembers in the options of the question, or the answer just received (it wins), only while the text has not changed
  if (settings.ai) {
    topics.forEach((topic) => {
      topic.Questions.forEach((question) => {
        const text: string | undefined = question.name?.[language]
        if (question.type !== "scale" || question.draft || !question.withScore || !text) return

        const suspect = getReverseSuspect(
          !!question.reverse,
          getValidMemory(settings.aiMemories?.[question.id] ?? question.options?.ai, text)
        )
        if (!suspect) return

        const title = t("template_issue_reverse_title")
        const description = t(
          suspect === "should-reverse" ? "template_issue_reverse_should" : "template_issue_reverse_should_not",
          { question: text.length > 80 ? text.slice(0, 77) + "..." : text }
        )
        issues.push({
          id: topic.id + ":reverse:" + question.id,
          kind: "reverse-suspect",
          severity: "warning",
          topicId: topic.id,
          label: text,
          group: topicNames[topic.id] + " · " + text,
          description,
          message: bold(title) + " : " + description,
          questionId: question.id,
          suggestedReverse: suspect === "should-reverse",
          needsConfirmation:
            (!settings.isSupervisor && !!question.fromSupervisor) || (!!settings.isLocked && !settings.isSupervisor)
        })
      })
    })
  }

  return issues
}
